import { createSlice } from '@reduxjs/toolkit';
import messageService from '../../utils/messageService';

const initialState = {
  conversations: [],
  messages: {},
  activeConversation: null,
  selectedConversation: null,
  conversationDetails: null,
  archivedConversations: [],
  messageHistory: [],
  searchResults: [],
  unreadCount: 0,
  blockedUsers: [],
  loading: false,
  error: null,
  searchQuery: '',
  isTyping: false,
  typingUsers: [],
  filters: {
    unreadOnly: false,
    archived: false
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  }
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setConversations: (state, action) => {
      state.conversations = action.payload.conversations || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setMessages: (state, action) => {
      const { conversationId, messages } = action.payload;
      state.messages[conversationId] = messages;
      state.loading = false;
      state.error = null;
    },
    setActiveConversation: (state, action) => {
      state.activeConversation = action.payload;
      state.loading = false;
      state.error = null;
    },
    setSelectedConversation: (state, action) => {
      state.selectedConversation = action.payload;
      state.loading = false;
      state.error = null;
    },
    setConversationDetails: (state, action) => {
      state.conversationDetails = action.payload;
      state.loading = false;
      state.error = null;
    },
    setArchivedConversations: (state, action) => {
      state.archivedConversations = action.payload.conversations || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setMessageHistory: (state, action) => {
      state.messageHistory = action.payload.messages || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload.messages || action.payload;
      state.pagination = action.payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
      state.loading = false;
      state.error = null;
    },
    setBlockedUsers: (state, action) => {
      state.blockedUsers = action.payload;
      state.loading = false;
      state.error = null;
    },
    addMessage: (state, action) => {
      const { conversationId, message } = action.payload;
      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }
      state.messages[conversationId].push(message);

      // Mettre à jour la conversation correspondante (support id et _id)
      const conversation = state.conversations.find(
        (conv) => (conv.id || conv._id) === conversationId
      );
      if (conversation) {
        conversation.lastMessage = message;
        conversation.updatedAt = message.createdAt || message.timestamp;
      }

      state.loading = false;
      state.error = null;
    },
    updateMessage: (state, action) => {
      const { conversationId, messageId, updates } = action.payload;
      if (state.messages[conversationId]) {
        const messageIndex = state.messages[conversationId].findIndex(msg => msg._id === messageId);
        if (messageIndex !== -1) {
          state.messages[conversationId][messageIndex] = {
            ...state.messages[conversationId][messageIndex],
            ...updates
          };
        }
      }
      state.loading = false;
      state.error = null;
    },
    removeMessage: (state, action) => {
      const { conversationId, messageId } = action.payload;
      if (state.messages[conversationId]) {
        state.messages[conversationId] = state.messages[conversationId].filter(
          msg => msg._id !== messageId
        );
      }
      state.loading = false;
      state.error = null;
    },
    addConversation: (state, action) => {
      state.conversations.unshift(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateConversation: (state, action) => {
      const payloadId = action.payload.id || action.payload._id;
      const index = state.conversations.findIndex(
        (conv) => (conv.id || conv._id) === payloadId
      );
      if (index !== -1) {
        state.conversations[index] = action.payload;
      }
      if (
        state.selectedConversation &&
        (state.selectedConversation.id || state.selectedConversation._id) === payloadId
      ) {
        state.selectedConversation = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeConversation: (state, action) => {
      const conversationId = action.payload;
      state.conversations = state.conversations.filter(
        (conv) => (conv.id || conv._id) !== conversationId
      );
      state.archivedConversations = state.archivedConversations.filter(
        (conv) => (conv.id || conv._id) !== conversationId
      );

      if ((state.activeConversation?.id || state.activeConversation?._id) === conversationId) {
        state.activeConversation = null;
      }
      if (
        state.selectedConversation &&
        (state.selectedConversation.id || state.selectedConversation._id) === conversationId
      ) {
        state.selectedConversation = null;
      }

      // Supprimer les messages de cette conversation
      delete state.messages[conversationId];

      state.loading = false;
      state.error = null;
    },
    markConversationAsRead: (state, action) => {
      const conversationId = typeof action.payload === 'object'
        ? action.payload.conversationId
        : action.payload;
      const conversation = state.conversations.find(
        (conv) => (conv.id || conv._id) === conversationId
      );
      if (conversation) {
        conversation.unreadCount = 0;
      }

      // Marquer tous les messages de cette conversation comme lus
      if (state.messages[conversationId]) {
        state.messages[conversationId].forEach((message) => {
          message.read = true;
        });
      }

      state.loading = false;
      state.error = null;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = '';
      state.searchResults = [];
    },
    setTyping: (state, action) => {
      state.isTyping = action.payload;
    },
    addTypingUser: (state, action) => {
      const userId = action.payload;
      if (!state.typingUsers.includes(userId)) {
        state.typingUsers.push(userId);
      }
    },
    removeTypingUser: (state, action) => {
      const userId = action.payload;
      state.typingUsers = state.typingUsers.filter(id => id !== userId);
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        unreadOnly: false,
        archived: false
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    toggleBlockUser: (state, action) => {
      const userId = action.payload;
      const index = state.blockedUsers.findIndex(id => id === userId);
      if (index !== -1) {
        state.blockedUsers.splice(index, 1);
      } else {
        state.blockedUsers.push(userId);
      }
    }
  }
});

// Actions asynchrones
export const fetchConversations = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await messageService.getConversations(params);
    dispatch(setConversations(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchConversationMessages = (conversationId, params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const messages = await messageService.getConversationMessages(conversationId, params);
    dispatch(setMessages({ conversationId, messages }));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const sendMessage = (conversationId, messageData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const message = await messageService.sendMessage(conversationId, messageData);
    dispatch(addMessage({ conversationId, message }));
    return message;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const sendMessageWithAttachment = (conversationId, formData) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const message = await messageService.sendMessageWithAttachment(conversationId, formData);
    dispatch(addMessage({ conversationId, message }));
    return message;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const markMessageAsRead = (conversationId, messageId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.markMessageAsRead(conversationId, messageId);
    dispatch(updateMessage({ 
      conversationId, 
      messageId, 
      updates: { read: true } 
    }));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const markConversationAsReadAction = (conversationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.markConversationAsRead(conversationId);
    dispatch(markConversationAsRead(conversationId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteMessage = (conversationId, messageId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.deleteMessage(conversationId, messageId);
    dispatch(removeMessage({ conversationId, messageId }));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const deleteConversation = (conversationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.deleteConversation(conversationId);
    dispatch(removeConversation(conversationId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const searchMessages = (query, params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await messageService.searchMessages(query, params);
    dispatch(setSearchResults(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const fetchUnreadCount = () => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const count = await messageService.getUnreadCount();
    dispatch(setUnreadCount(count));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const blockUser = (userId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.blockUser(userId);
    dispatch(toggleBlockUser(userId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const unblockUser = (userId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.unblockUser(userId);
    dispatch(toggleBlockUser(userId));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const createConversation = (participantIds) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const conversation = await messageService.createConversation(participantIds);
    dispatch(addConversation(conversation));
    return conversation;
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchConversationDetails = (conversationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const details = await messageService.getConversationDetails(conversationId);
    dispatch(setConversationDetails(details));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const archiveConversation = (conversationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedConversation = await messageService.archiveConversation(conversationId);
    dispatch(updateConversation(updatedConversation));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const unarchiveConversation = (conversationId) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const updatedConversation = await messageService.unarchiveConversation(conversationId);
    dispatch(updateConversation(updatedConversation));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchArchivedConversations = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await messageService.getArchivedConversations(params);
    dispatch(setArchivedConversations(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

export const reportMessage = (conversationId, messageId, reason) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    await messageService.reportMessage(conversationId, messageId, reason);
    // Optionnel : marquer le message comme signalé dans l'état local
    dispatch(updateMessage({ 
      conversationId, 
      messageId, 
      updates: { reported: true } 
    }));
  } catch (error) {
    dispatch(setError(error.message));
    throw error;
  }
};

export const fetchMessageHistory = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  try {
    const response = await messageService.getMessageHistory(params);
    dispatch(setMessageHistory(response));
  } catch (error) {
    dispatch(setError(error.message));
  }
};

// Sélecteurs
export const selectConversations = (state) => state.messages.conversations;
export const selectMessages = (state) => state.messages.messages;
export const selectActiveConversation = (state) => state.messages.activeConversation;
export const selectSelectedConversation = (state) => state.messages.selectedConversation;
export const selectConversationDetails = (state) => state.messages.conversationDetails;
export const selectArchivedConversations = (state) => state.messages.archivedConversations;
export const selectMessageHistory = (state) => state.messages.messageHistory;
export const selectSearchResults = (state) => state.messages.searchResults;
export const selectUnreadCount = (state) => state.messages.unreadCount;
export const selectBlockedUsers = (state) => state.messages.blockedUsers;
export const selectMessagesLoading = (state) => state.messages.loading;
export const selectMessagesError = (state) => state.messages.error;
export const selectSearchQuery = (state) => state.messages.searchQuery;
export const selectIsTyping = (state) => state.messages.isTyping;
export const selectTypingUsers = (state) => state.messages.typingUsers;
export const selectMessagesFilters = (state) => state.messages.filters;
export const selectMessagesPagination = (state) => state.messages.pagination;

// Sélecteur pour les conversations filtrées
export const selectFilteredConversations = (state) => {
  const { conversations, searchResults, searchQuery } = state.messages;
  return searchQuery ? (searchResults || []) : (conversations || []);
};

// Sélecteur pour le nombre total de messages non lus
export const selectTotalUnreadCount = (state) => {
  const filteredConversations = selectFilteredConversations(state);
  return filteredConversations.reduce((total, conv) => total + (conv.unreadCount || 0), 0);
};

export const {
  setLoading,
  setError,
  clearError,
  setConversations,
  setMessages,
  setActiveConversation,
  setSelectedConversation,
  setConversationDetails,
  setArchivedConversations,
  setMessageHistory,
  setSearchResults,
  setUnreadCount,
  setBlockedUsers,
  addMessage,
  updateMessage,
  removeMessage,
  addConversation,
  updateConversation,
  removeConversation,
  markConversationAsRead,
  setSearchQuery,
  clearSearch,
  setTyping,
  addTypingUser,
  removeTypingUser,
  setFilters,
  clearFilters,
  setPagination,
  toggleBlockUser
} = messagesSlice.actions;

export default messagesSlice.reducer;